import { AlertTriangle, X } from 'lucide-react'
import { toast as sonnerToast } from 'sonner'
import { useCallback, useEffect, useRef, useState } from 'react'

interface ICustomToastProgress {
  title: string
  description: string
  toastId: string | number
}

export const CustomToastProgress = ({
  title,
  description,
  toastId,
}: ICustomToastProgress) => {
  const duration = 6000
  const [progress, setProgress] = useState(100)
  const [isPaused, setIsPaused] = useState(false)
  const startTimeRef = useRef(Date.now())
  const pauseTimeRef = useRef(0)
  const totalPausedTimeRef = useRef(0)
  const animationFrameRef = useRef<number>()

  const updateProgress = useCallback(() => {
    const currentTime = Date.now()
    const elapsedTime =
      currentTime - startTimeRef.current - totalPausedTimeRef.current
    const newProgress = Math.max(0, 100 - (elapsedTime * 100) / duration)

    setProgress(newProgress)

    if (newProgress > 0) {
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    } else {
      sonnerToast.dismiss(toastId)
    }
  }, [duration, toastId])

  useEffect(() => {
    if (isPaused) {
      pauseTimeRef.current = Date.now()
      cancelAnimationFrame(animationFrameRef.current!)
    } else {
      if (pauseTimeRef.current > 0) {
        totalPausedTimeRef.current += Date.now() - pauseTimeRef.current
        pauseTimeRef.current = 0
      }
      animationFrameRef.current = requestAnimationFrame(updateProgress)
    }

    return () => {
      cancelAnimationFrame(animationFrameRef.current!)
    }
  }, [isPaused, updateProgress])

  const handleMouseEnter = () => {
    return setIsPaused(true)
  }
  const handleMouseLeave = () => setIsPaused(false)
  const handleClose = () => {
    cancelAnimationFrame(animationFrameRef.current!)
    sonnerToast.dismiss(toastId)
  }

  return (
    <div
      className="flex w-full flex-col max-w-md overflow-hidden rounded-lg bg-white shadow-lg md:min-w-[372px] p-6"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-[#FFA756]rounded-full w-8 h-10">
            <AlertTriangle className="text-[#FFA756]" />
          </div>
          <div className="text-lg font-semibold text-gray-900">{title}</div>
        </div>

        <button
          onClick={handleClose}
          className="p-1 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </button>
      </div>

      <div className="space-y-3 text-gray-600">
        <p className="leading-relaxed text-sm">{description}</p>
      </div>

      <div className="mt-4 w-full bg-gray-200 h-1 rounded-full overflow-hidden">
        <div
          className="h-full bg-primary transition-[width] duration-100 ease-linear"
          style={{
            width: `${progress.toFixed(0)}%`,
          }}
        />
      </div>
    </div>
  )
}
